import { apiFetch } from '../apiClient';

export interface SuperadminUserListItem {
  id: number;
  profilePicture?: string | null;
  userName: string;
  email: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  workspaces: string;
  totalRecordings: number;
  monthlyRecordings: number;
  totalHours: number;
  monthlyHours: number;
  totalComments: number;
  monthlyComments: number;
}

export async function fetchUsers(params: { search?: string; limit?: number; offset?: number; sort?: string; order?: 'asc' | 'desc' } = {}) {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  const res = await apiFetch(`/superadmin/users${qs.toString() ? `?${qs.toString()}` : ''}`);
  if (!res.ok) throw new Error(`Failed to load users: ${res.status}`);
  const raw = await res.json();
  // Map snake_case keys from backend to camelCase interface used by the UI
  return (Array.isArray(raw) ? raw : []).map((u: any) => ({
    id: Number(u.id),
    profilePicture: u.profile_picture ?? u.profilePicture ?? null,
    userName: u.user_name ?? u.userName ?? '',
    email: u.email ?? '',
    isAdmin: Boolean(u.is_admin ?? u.isAdmin ?? false),
    isSuperAdmin: Boolean(u.is_super_admin ?? u.isSuperAdmin ?? false),
    workspaces: u.workspaces ?? '',
    totalRecordings: Number(u.total_recordings ?? u.totalRecordings ?? 0),
    monthlyRecordings: Number(u.monthly_recordings ?? u.monthlyRecordings ?? 0),
    totalHours: Number(u.total_hours ?? u.totalHours ?? 0),
    monthlyHours: Number(u.monthly_hours ?? u.monthlyHours ?? 0),
    totalComments: Number(u.total_comments ?? u.totalComments ?? 0),
    monthlyComments: Number(u.monthly_comments ?? u.monthlyComments ?? 0),
  })) as SuperadminUserListItem[];
}

export async function fetchUserDetail(userId: number) {
  const res = await apiFetch(`/superadmin/users/${userId}`);
  if (!res.ok) throw new Error(`Failed to load user ${userId}`);
  return await res.json();
}

export async function patchUserAdminFlags(
  userId: number,
  updates: { isAdmin?: boolean; isSuperAdmin?: boolean },
) {
  const res = await apiFetch(`/superadmin/users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`Failed to update admin flags: ${res.status}`);
  return await res.json();
}

export async function deleteUser(userId: number) {
  const res = await apiFetch(`/superadmin/users/${userId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to delete user: ${res.status}`);
  return await res.json();
}

export async function fetchUserVideos(userId: number, params: { search?: string; limit?: number; offset?: number; sort?: string; order?: 'asc' | 'desc' } = {}) {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  const res = await apiFetch(`/superadmin/users/${userId}/videos${qs.toString() ? `?${qs.toString()}` : ''}`);
  if (!res.ok) throw new Error(`Failed to load user videos: ${res.status}`);
  return await res.json();
}

export async function fetchUserComments(userId: number, params: { search?: string; limit?: number; offset?: number; sort?: string; order?: 'asc' | 'desc' } = {}) {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  const res = await apiFetch(`/superadmin/users/${userId}/comments${qs.toString() ? `?${qs.toString()}` : ''}`);
  if (!res.ok) throw new Error(`Failed to load user comments: ${res.status}`);
  return await res.json();
}

export async function adminDeleteVideo(videoId: number) {
  const res = await apiFetch(`/superadmin/videos/${videoId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to delete video: ${res.status}`);
  return await res.json();
}

export async function adminDeleteComment(commentId: number) {
  const res = await apiFetch(`/superadmin/comments/${commentId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to delete comment: ${res.status}`);
  return await res.json();
}

